import { useRef, useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Layers, CheckCircle2, AlertCircle, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { cn } from './ui/utils';
import { SPACE_TYPE_OPTIONS, jiraSpaceUrlProblem, spaceTypeOption, type SpaceType } from '../utils/spaceTeams';
import { SpaceKeyGuidance } from './SpaceKeyGuidance';

export interface ExistingSpaceRef {
  id?: string;
  name: string;
  key?: string;
  status?: string;
}

interface CreateSpaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  accessToken: string;
  consultantId: string;
  /** Active spaces for this company (non-archived); used for live duplicate checks */
  existingSpaces?: ExistingSpaceRef[];
  onSpaceCreated: () => void | Promise<void>;
}

function normName(s: string) {
  return s.trim().toLowerCase();
}

function normKey(s: string) {
  return s.trim().toUpperCase();
}

function companyIdsMatch(a: string, b: string) {
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

function isNonArchivedSpace(status?: string) {
  return (status ?? 'active') !== 'archived';
}

export function CreateSpaceDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  accessToken,
  consultantId,
  existingSpaces = [],
  onSpaceCreated,
}: CreateSpaceDialogProps) {
  const [loading, setLoading] = useState(false);
  const submitInFlightRef = useRef(false);
  /** Authoritative list for duplicates: fetched when dialog opens + merged with parent prop */
  const [fetchedExisting, setFetchedExisting] = useState<ExistingSpaceRef[]>([]);
  const [existingFetchState, setExistingFetchState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const [formData, setFormData] = useState({
    name: '',
    type: 'jsm' as SpaceType,
    key: '',
    description: '',
    teams: '',
    jiraSpaceUrl: '',
  });
  const [keyGuidanceOpen, setKeyGuidanceOpen] = useState(false);
  const urlProblem = jiraSpaceUrlProblem(formData.jiraSpaceUrl);

  const nameTrim = formData.name.trim();
  const keyNorm = normKey(formData.key);

  useEffect(() => {
    if (!open || !String(companyId).trim()) {
      setFetchedExisting([]);
      setExistingFetchState('idle');
      return;
    }
    let cancelled = false;
    setExistingFetchState('loading');
    (async () => {
      try {
        const res = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/spaces/all`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!res.ok) {
          if (!cancelled) setExistingFetchState('error');
          return;
        }
        const data = (await res.json()) as { spaces?: unknown };
        const all = Array.isArray(data.spaces) ? data.spaces : [];
        const mine: ExistingSpaceRef[] = [];
        for (const raw of all as Array<Record<string, unknown>>) {
          const sid = raw.companyId != null ? String(raw.companyId) : '';
          if (!companyIdsMatch(sid, companyId)) continue;
          if (!isNonArchivedSpace(raw.status as string | undefined)) continue;
          mine.push({
            id: raw.id != null ? String(raw.id) : undefined,
            name: String(raw.name ?? ''),
            key: raw.key != null ? String(raw.key) : undefined,
            status: raw.status != null ? String(raw.status) : undefined,
          });
        }
        if (!cancelled) {
          setFetchedExisting(mine);
          setExistingFetchState('ready');
        }
      } catch {
        if (!cancelled) setExistingFetchState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, accessToken]);

  const combinedExisting = useMemo(() => {
    const byId = new Map<string, ExistingSpaceRef>();
    const add = (s: ExistingSpaceRef) => {
      const dedupeKey =
        s.id ||
        `n:${normName(s.name)}:k:${normKey(String(s.key ?? '__nokey__'))}`;
      if (!byId.has(dedupeKey)) byId.set(dedupeKey, s);
    };
    for (const s of fetchedExisting) add(s);
    for (const s of existingSpaces) add(s);
    return Array.from(byId.values());
  }, [fetchedExisting, existingSpaces]);

  /** We can show duplicate / "available" states once we are not waiting on first fetch (unless parent already gave us rows). */
  const duplicateUiReady =
    existingSpaces.length > 0 ||
    existingFetchState === 'ready' ||
    existingFetchState === 'error';

  const nameDuplicate = useMemo(() => {
    if (!nameTrim) return false;
    const n = normName(nameTrim);
    return combinedExisting.some(
      (s) => isNonArchivedSpace(s.status) && normName(s.name) === n,
    );
  }, [nameTrim, combinedExisting]);

  const keyDuplicate = useMemo(() => {
    if (!keyNorm || keyNorm.length < 2) return false;
    return combinedExisting.some(
      (s) => isNonArchivedSpace(s.status) && normKey(String(s.key ?? '')) === keyNorm,
    );
  }, [keyNorm, combinedExisting]);

  const nameShowsOk =
    duplicateUiReady && nameTrim.length > 0 && !nameDuplicate;
  const keyFormatOk = keyNorm.length === 0 || /^[A-Z0-9]+$/.test(keyNorm);
  const keyLengthOk = keyNorm.length >= 2 && keyNorm.length <= 10;
  const keyShowsOk =
    duplicateUiReady && keyNorm.length > 0 && keyFormatOk && keyLengthOk && !keyDuplicate;

  const canSubmit =
    duplicateUiReady &&
    nameTrim.length > 0 &&
    !nameDuplicate &&
    keyLengthOk &&
    keyFormatOk &&
    !keyDuplicate &&
    !urlProblem;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitInFlightRef.current || loading) return;

    if (!nameTrim) {
      toast.error('Please enter a space name');
      return;
    }

    if (nameDuplicate) {
      toast.error('This space name is already in use for this client.');
      return;
    }

    if (!formData.key.trim()) {
      toast.error('Please enter a space key');
      return;
    }

    if (!/^[A-Z0-9]+$/.test(formData.key)) {
      toast.error('Space key must contain only uppercase letters and numbers');
      return;
    }

    if (keyNorm.length < 2 || keyNorm.length > 10) {
      toast.error('Space key must be 2–10 characters');
      return;
    }

    if (keyDuplicate) {
      toast.error('This space key is already in use for this client.');
      return;
    }

    if (urlProblem) {
      toast.error(urlProblem);
      return;
    }

    try {
      submitInFlightRef.current = true;
      setLoading(true);

      const spaceData = {
        name: nameTrim,
        type: formData.type,
        key: keyNorm,
        description: formData.description.trim(),
        teams: formData.teams.split(',').map((t) => t.trim()).filter(Boolean),
        jiraSpaceUrl: formData.jiraSpaceUrl.trim(),
      };

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/space-discovery/${companyId}/create-spaces`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            spaces: [spaceData],
          }),
        },
      );

      if (!response.ok) {
        const text = await response.text();
        let message = 'Failed to create space';
        try {
          const parsed = JSON.parse(text) as { error?: string };
          if (typeof parsed?.error === 'string' && parsed.error.length > 0) {
            message = parsed.error;
          }
        } catch {
          if (text.trim()) message = text.trim().slice(0, 300);
        }
        throw new Error(message);
      }

      let result: any = null;
      try {
        result = await response.json();
      } catch {
        // Some success responses can be empty/non-JSON in edge deployments.
      }

      if (result && typeof result === 'object' && result.success === false) {
        throw new Error(result.error || 'Failed to create space');
      }

      toast.success(`Space "${formData.name}" created`);

      setFormData({
        name: '',
        type: 'jsm',
        key: '',
        description: '',
        teams: '',
        jiraSpaceUrl: '',
      });

      onOpenChange(false);
      await onSpaceCreated();
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'Failed to create space';
      toast.error(msg);
    } finally {
      setLoading(false);
      submitInFlightRef.current = false;
    }
  };

  const handleKeyChange = (value: string) => {
    const sanitized = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setFormData((prev) => ({ ...prev, key: sanitized }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="size-5 text-blue-600" />
            Create New Space for {companyName}
          </DialogTitle>
          <DialogDescription>
            Create a JSM, Jira Software, Jira Product Discovery or Confluence space for this client. Spaces help
            organize teams, configuration items and workflows.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="type">Space Type *</Label>
            <Select
              value={formData.type}
              onValueChange={(value: SpaceType) =>
                setFormData((prev) => ({ ...prev, type: value }))
              }
            >
              <SelectTrigger id="type" aria-label="Space Type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPACE_TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground" data-testid="space-type-hint">
              {spaceTypeOption(formData.type)?.description}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Space Name *</Label>
            <Input
              id="name"
              placeholder="e.g., IT Service Desk, HR Portal, Marketing Team"
              value={formData.name}
              autoComplete="off"
              aria-invalid={nameDuplicate}
              aria-describedby="create-space-name-hint"
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              disabled={loading}
              className={cn(nameDuplicate && 'border-destructive')}
            />
            <p id="create-space-name-hint" className="text-xs flex items-start gap-1.5 min-h-[1.25rem]">
              {!duplicateUiReady ? (
                <span className="text-muted-foreground">Loading existing spaces for duplicate check…</span>
              ) : nameDuplicate ? (
                <>
                  <AlertCircle className="size-3.5 shrink-0 text-destructive mt-0.5" aria-hidden />
                  <span className="text-destructive">
                    A space with this name already exists for this client. Choose a different name.
                  </span>
                </>
              ) : nameShowsOk ? (
                <>
                  <CheckCircle2 className="size-3.5 shrink-0 text-emerald-600 mt-0.5" aria-hidden />
                  <span className="text-emerald-700 dark:text-emerald-600">This name is available.</span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Must be unique for this client (checked as you type).
                </span>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="key">Space Key *</Label>
              <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setKeyGuidanceOpen(true)}>
                <HelpCircle className="size-3.5 mr-1" aria-hidden />
                Key guidance
              </Button>
            </div>
            <Input
              id="key"
              placeholder="e.g., ITSD, HR, MKTG (uppercase letters and numbers only)"
              value={formData.key}
              aria-invalid={keyDuplicate || (formData.key.length > 0 && !keyFormatOk)}
              aria-describedby="create-space-key-hint"
              onChange={(e) => handleKeyChange(e.target.value)}
              disabled={loading}
              maxLength={10}
              className={cn((keyDuplicate || (formData.key.length > 0 && !keyFormatOk)) && 'border-destructive')}
            />
            <p id="create-space-key-hint" className="text-xs flex items-start gap-1.5 min-h-[1.25rem]">
              {!duplicateUiReady ? (
                <span className="text-muted-foreground">Loading existing spaces for duplicate check…</span>
              ) : formData.key.length > 0 && !keyFormatOk ? (
                <>
                  <AlertCircle className="size-3.5 shrink-0 text-destructive mt-0.5" aria-hidden />
                  <span className="text-destructive">Only uppercase letters and numbers are allowed.</span>
                </>
              ) : keyDuplicate ? (
                <>
                  <AlertCircle className="size-3.5 shrink-0 text-destructive mt-0.5" aria-hidden />
                  <span className="text-destructive">
                    This key is already used by another space. Choose a different key.
                  </span>
                </>
              ) : keyShowsOk ? (
                <>
                  <CheckCircle2 className="size-3.5 shrink-0 text-emerald-600 mt-0.5" aria-hidden />
                  <span className="text-emerald-700 dark:text-emerald-600">This key is available.</span>
                </>
              ) : (
                <span className="text-muted-foreground">
                  Unique per client, 2–10 characters. Letters and numbers only; checked as you type.
                </span>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the purpose of this space..."
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              disabled={loading}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="jira-space-url">Jira Space URL (Optional)</Label>
            <Input
              id="jira-space-url"
              placeholder="e.g., https://yourcompany.atlassian.net/jira/servicedesk/projects/SD"
              value={formData.jiraSpaceUrl}
              autoComplete="off"
              aria-invalid={!!urlProblem}
              onChange={(e) => setFormData((prev) => ({ ...prev, jiraSpaceUrl: e.target.value }))}
              disabled={loading}
              className={cn(urlProblem && 'border-destructive')}
            />
            <p className={cn('text-xs', urlProblem ? 'text-destructive' : 'text-muted-foreground')}>
              {urlProblem || 'Link to the space in Jira, for easy reference during implementation'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="teams">Teams (Optional)</Label>
            <Input
              id="teams"
              placeholder="e.g., IT Support, Network Team, Security (comma-separated)"
              value={formData.teams}
              onChange={(e) => setFormData((prev) => ({ ...prev, teams: e.target.value }))}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              Enter team names separated by commas; add a description to each team from the space's Teams list
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !canSubmit}>
              {loading && <Loader2 className="size-4 mr-2 animate-spin" />}
              {loading ? 'Creating Space...' : 'Create Space'}
            </Button>
          </div>
        </form>
        <SpaceKeyGuidance open={keyGuidanceOpen} onClose={() => setKeyGuidanceOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}
